/** 数据集列表 · 状态筛选项（与接口 status 1/2 对齐）。 */
export const DATASET_STATUS_OPTIONS = [
  { label: '上线', value: '1' },
  { label: '下线', value: '2' },
]

export function getDatasetStatusLabel(status) {
  return Number(status) === 1 ? '上线' : '下线'
}

export function getDatasetStatusPillVariant(status) {
  return Number(status) === 1 ? 'online' : 'offline'
}

/** 列表接口用 Default 表示「不做该维度过滤」，与详情页「未配置」同义。 */
export function isDefaultFilterValue(value) {
  const raw = String(value ?? '').trim()
  return !raw || raw === 'Default'
}

/**
 * 解析国家过滤原始值。
 * 列表接口：未配置时返回 Default；已配置时仍可能返回 JSON 字符串。
 * 详情接口：返回 DB 原始 JSON。
 */
export function parseCountryFilterRaw(country) {
  if (country == null || country === '') {
    return null
  }

  if (typeof country === 'object' && !Array.isArray(country)) {
    return {
      isExclude: country.isExclude !== false,
      countries: Array.isArray(country.countries) ? country.countries.filter(Boolean) : [],
    }
  }

  const raw = String(country).trim()
  if (!raw || raw === 'Default' || raw === '{}') {
    return null
  }

  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return {
        isExclude: false,
        countries: parsed.filter(Boolean),
      }
    }
    if (parsed && typeof parsed === 'object') {
      return {
        isExclude: parsed.isExclude !== false,
        countries: Array.isArray(parsed.countries) ? parsed.countries.filter(Boolean) : [],
      }
    }
  } catch (error) {
    // 列表展示容错：无法解析时按未配置处理。
  }

  return null
}

/** 国家过滤摘要（列表 + 详情通用）。 */
export function formatCountryDisplay(country) {
  if (isDefaultFilterValue(country)) {
    return '默认'
  }

  const parsed = parseCountryFilterRaw(country)
  if (!parsed || !parsed.countries.length) {
    return '默认'
  }

  const mode = parsed.isExclude ? '不包含' : '包含'
  return `${mode}：${parsed.countries.join(', ')}`
}

/**
 * 列表接口 dealwithVersion 已把版本预处理为：
 * - Default
 * - "start-end"
 * - "start-end跳过skip"
 */
export function normalizeListVersionDisplay(versionRaw) {
  const raw = String(versionRaw ?? '').trim()
  if (isDefaultFilterValue(raw)) {
    return '默认'
  }

  const skipIndex = raw.indexOf('跳过')
  let rangePart = raw
  let skipPart = ''

  if (skipIndex >= 0) {
    rangePart = raw.slice(0, skipIndex).trim()
    skipPart = raw.slice(skipIndex + 2).trim()
  }

  if (!rangePart || rangePart === '-') {
    return skipPart ? `跳过 ${skipPart}` : '默认'
  }

  const dashIndex = rangePart.indexOf('-')
  if (dashIndex >= 0) {
    const start = rangePart.slice(0, dashIndex).trim()
    const end = rangePart.slice(dashIndex + 1).trim()

    if (!start && !end) {
      return skipPart ? `跳过 ${skipPart}` : '默认'
    }

    let range = ''
    if (start && end) {
      range = `${start}–${end}`
    } else if (start) {
      range = `${start} 起`
    } else {
      range = `至 ${end}`
    }

    return skipPart ? `${range} · 跳过 ${skipPart}` : range
  }

  return skipPart ? `${rangePart} · 跳过 ${skipPart}` : rangePart
}

/**
 * 解析版本过滤原始值（详情接口 JSON 形态）。
 */
export function parseVersionFilterRaw(versionRaw) {
  if (versionRaw == null || versionRaw === '') {
    return null
  }

  if (typeof versionRaw === 'object') {
    return {
      start: versionRaw.start ?? '',
      end: versionRaw.end ?? '',
      skip: versionRaw.skip ?? '',
    }
  }

  const raw = String(versionRaw).trim()
  if (!raw || raw === 'Default' || raw === '{}') {
    return null
  }

  if (!raw.startsWith('{') && !raw.startsWith('[')) {
    return null
  }

  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        start: parsed.start ?? '',
        end: parsed.end ?? '',
        skip: parsed.skip ?? '',
      }
    }
  } catch (error) {
    // 列表展示容错。
  }

  return null
}

function formatVersionFilterFromJson(versionRaw) {
  const parsed = parseVersionFilterRaw(versionRaw)
  if (!parsed) {
    return '默认'
  }

  const start = parsed.start !== '' && parsed.start != null ? parsed.start : null
  const end = parsed.end !== '' && parsed.end != null ? parsed.end : null
  const skip = String(parsed.skip || '').trim()

  if (start == null && end == null && !skip) {
    return '默认'
  }

  const parts = []
  if (start != null || end != null) {
    parts.push(`${start ?? '—'}–${end ?? '—'}`)
  }
  if (skip) {
    parts.push(`跳过 ${skip}`)
  }
  return parts.join(' · ')
}

/**
 * 应用版本 / 系统版本过滤摘要。
 * 优先识别列表接口预处理字符串，再回退 JSON 解析（详情形态）。
 */
export function formatVersionFilterDisplay(versionRaw) {
  const raw = String(versionRaw ?? '').trim()
  if (isDefaultFilterValue(raw)) {
    return '默认'
  }

  if (!raw.startsWith('{') && !raw.startsWith('[')) {
    return normalizeListVersionDisplay(raw)
  }

  return formatVersionFilterFromJson(versionRaw)
}

export function findAppOptionLabel(appOptions, appId) {
  const id = Number(appId)
  const matched = (appOptions || []).find((item) => Number(item.id) === id)
  return matched && matched.name ? matched.name : '—'
}
